import React, { useState } from 'react';
import {
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Slider,
  Switch,
  Box,
  Typography,
  Divider,
  Alert,
  Tabs,
  TabList,
  Tab,
  TabPanel,
} from '@mui/joy';
import {
  useCreateStormTerritory,
  useUpdateStormTerritory,
} from '../../hooks/useStormTracker';
import { TerritoryMap } from './TerritoryMap';
import type { StormTerritory, StormTerritoryFormData } from '../../types/stormtracker';

interface TerritoryFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  territory?: StormTerritory;
}

const DEFAULT_FORM_DATA: StormTerritoryFormData = {
  name: '',
  zip_codes: [],
  min_hail_size_inches: 1.0,
  min_wind_speed_mph: 58,
  include_tornadoes: true,
  notify_via_sms: true,
  notify_via_push: false,
  is_active: true,
};

const getInitialFormData = (territory?: StormTerritory): StormTerritoryFormData => {
  if (!territory) return DEFAULT_FORM_DATA;
  return {
    name: territory.name,
    zip_codes: territory.zip_codes,
    counties: territory.counties,
    center_latitude: territory.center_latitude,
    center_longitude: territory.center_longitude,
    radius_miles: territory.radius_miles,
    min_hail_size_inches: territory.min_hail_size_inches,
    min_wind_speed_mph: territory.min_wind_speed_mph,
    include_tornadoes: territory.include_tornadoes,
    notify_via_sms: territory.notify_via_sms,
    notify_via_push: territory.notify_via_push,
    is_active: territory.is_active,
    quiet_hours_start: territory.quiet_hours_start,
    quiet_hours_end: territory.quiet_hours_end,
  };
};

function settingsError(error: unknown, fallback: string): string {
  const data = (error as { responseData?: Record<string, unknown> })?.responseData;
  if (data) {
    const message = Object.entries(data).map(([field, value]) => {
      if (field === 'code') return '';
      const detail = Array.isArray(value) ? value.join(' ') : typeof value === 'string' ? value : '';
      if (!detail) return '';
      return ['detail', 'error', 'non_field_errors'].includes(field)
        ? detail : `${field.replaceAll('_', ' ')}: ${detail}`;
    }).filter(Boolean).join(' ');
    if (message) return message;
  }
  return error instanceof Error ? error.message : fallback;
}

// Inner form content component - resets when key changes
interface FormContentProps {
  territory?: StormTerritory;
  onClose: () => void;
  onSuccess?: () => void;
}

const FormContent: React.FC<FormContentProps> = ({ territory, onClose, onSuccess }) => {
  const [formData, setFormData] = useState<StormTerritoryFormData>(() => getInitialFormData(territory));
  const [zipCodesInput, setZipCodesInput] = useState(() => territory?.zip_codes?.join(', ') || '');
  const [locationTab, setLocationTab] = useState(() => territory?.radius_miles ? 1 : 0);
  const [error, setError] = useState<string | null>(null);

  const createMutation = useCreateStormTerritory();
  const updateMutation = useUpdateStormTerritory(territory?.id ?? 0);

  const isEditing = !!territory;
  const isAuto = !!territory?.is_auto_generated;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Auto-generated watchlists: only alerting/notification fields are editable.
    // ZIP list and name are derived from the rep's activity and must not be
    // sent on the PATCH (backend would reject).
    if (isAuto && territory) {
      const autoData: Partial<StormTerritoryFormData> = {
        min_hail_size_inches: formData.min_hail_size_inches,
        min_wind_speed_mph: formData.min_wind_speed_mph,
        include_tornadoes: formData.include_tornadoes,
        notify_via_sms: formData.notify_via_sms,
        notify_via_push: formData.notify_via_push,
        quiet_hours_start: formData.quiet_hours_start,
        quiet_hours_end: formData.quiet_hours_end,
        is_active: formData.is_active,
      };
      try {
        await updateMutation.mutateAsync(autoData);
        onSuccess?.();
        onClose();
      } catch (err: unknown) {
        setError(settingsError(err, 'Failed to save alert settings'));
      }
      return;
    }

    // Parse ZIP codes from input
    const zipCodes = zipCodesInput
      .split(/[,\s]+/)
      .map((z) => z.trim())
      .filter((z) => /^\d{5}$/.test(z));

    const submitData: StormTerritoryFormData = {
      ...formData,
      zip_codes: locationTab === 0 ? zipCodes : undefined,
      center_latitude: locationTab === 1 ? formData.center_latitude : undefined,
      center_longitude: locationTab === 1 ? formData.center_longitude : undefined,
      radius_miles: locationTab === 1 ? formData.radius_miles : undefined,
    };

    // Validation
    if (!submitData.name.trim()) {
      setError('Territory name is required');
      return;
    }

    if (locationTab === 0 && (!zipCodes || zipCodes.length === 0)) {
      setError('At least one valid ZIP code is required');
      return;
    }

    if (locationTab === 1 && (!submitData.center_latitude || !submitData.center_longitude)) {
      setError('Please select a center point on the map');
      return;
    }

    try {
      if (isEditing) {
        await updateMutation.mutateAsync(submitData);
      } else {
        await createMutation.mutateAsync(submitData);
      }
      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      setError(settingsError(err, 'Failed to save territory'));
    }
  };

  const handleCenterChange = (lat: number, lng: number) => {
    setFormData((prev) => ({
      ...prev,
      center_latitude: lat,
      center_longitude: lng,
    }));
  };

  const handleRadiusChange = (radius: number) => {
    setFormData((prev) => ({
      ...prev,
      radius_miles: radius,
    }));
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <>
      <ModalClose />
      <DialogTitle>
        {isAuto ? 'Edit Alert Settings' : isEditing ? 'Edit Territory' : 'Create Territory'}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert color="danger" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {isAuto && (
            <Alert color="primary" sx={{ mb: 2 }}>
              This watchlist's ZIP codes are derived from your knocks and
              opportunities. You can still tune the alert thresholds and
              notification preferences here.
            </Alert>
          )}

          {!isAuto && (
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Territory Name</FormLabel>
              <Input
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., North Dallas Service Area"
                required
              />
            </FormControl>
          )}

          {!isAuto && (
            <Divider sx={{ my: 2 }}>
              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                Geographic Scope
              </Typography>
            </Divider>
          )}

          {!isAuto && (
          <Tabs value={locationTab} onChange={(_, value) => setLocationTab(value as number)} sx={{ mb: 2 }}>
            <TabList>
              <Tab>ZIP Codes</Tab>
              <Tab>Radius from Point</Tab>
            </TabList>

            <TabPanel value={0} sx={{ p: 2 }}>
              <FormControl>
                <FormLabel>ZIP Codes (comma-separated)</FormLabel>
                <Textarea
                  value={zipCodesInput}
                  onChange={(e) => setZipCodesInput(e.target.value)}
                  placeholder="75001, 75002, 75023, 75024"
                  minRows={2}
                />
                <Typography level="body-xs" sx={{ color: 'text.secondary', mt: 0.5 }}>
                  Enter 5-digit ZIP codes separated by commas
                </Typography>
              </FormControl>
            </TabPanel>

            <TabPanel value={1} sx={{ p: 2 }}>
              <Box sx={{ mb: 2 }}>
                <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 1 }}>
                  Click on the map to set the center point, or drag the marker
                </Typography>
                <TerritoryMap
                  height={250}
                  editable
                  centerLat={formData.center_latitude}
                  centerLng={formData.center_longitude}
                  radiusMiles={formData.radius_miles || 25}
                  onCenterChange={handleCenterChange}
                  onRadiusChange={handleRadiusChange}
                />
              </Box>
              <FormControl>
                <FormLabel>Radius: {formData.radius_miles || 25} miles</FormLabel>
                <Slider
                  value={formData.radius_miles || 25}
                  onChange={(_, value) => setFormData((prev) => ({ ...prev, radius_miles: value as number }))}
                  min={5}
                  max={100}
                  step={5}
                  marks={[
                    { value: 5, label: '5mi' },
                    { value: 25, label: '25mi' },
                    { value: 50, label: '50mi' },
                    { value: 100, label: '100mi' },
                  ]}
                />
              </FormControl>
            </TabPanel>
          </Tabs>
          )}

          <Divider sx={{ my: 2 }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Alert Thresholds
            </Typography>
          </Divider>

          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Minimum Hail Size: {formData.min_hail_size_inches}" (Insurance threshold: 1")</FormLabel>
            <Slider
              value={formData.min_hail_size_inches}
              onChange={(_, value) => setFormData((prev) => ({ ...prev, min_hail_size_inches: value as number }))}
              min={0.5}
              max={3}
              step={0.25}
              marks={[
                { value: 0.5, label: '0.5"' },
                { value: 1, label: '1" (ins.)' },
                { value: 2, label: '2"' },
                { value: 3, label: '3"' },
              ]}
            />
          </FormControl>

          <FormControl sx={{ mb: 2 }}>
            <FormLabel>Minimum Wind Speed: {formData.min_wind_speed_mph} mph (Severe: 58+)</FormLabel>
            <Slider
              value={formData.min_wind_speed_mph}
              onChange={(_, value) => setFormData((prev) => ({ ...prev, min_wind_speed_mph: value as number }))}
              min={40}
              max={100}
              step={2}
              marks={[
                { value: 40, label: '40mph' },
                { value: 58, label: '58 (sev.)' },
                { value: 75, label: '75mph' },
                { value: 100, label: '100mph' },
              ]}
            />
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography level="body-md">Include Tornado Alerts</Typography>
            <Switch
              slotProps={{ input: { 'aria-label': 'Include Tornado Alerts' } }}
              checked={formData.include_tornadoes}
              onChange={(e) => setFormData((prev) => ({ ...prev, include_tornadoes: e.target.checked }))}
            />
          </Box>

          <Divider sx={{ my: 2 }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Notifications
            </Typography>
          </Divider>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography level="body-md">Active (monitoring enabled)</Typography>
            <Switch
              checked={formData.is_active}
              onChange={(e) => setFormData((prev) => ({ ...prev, is_active: e.target.checked }))}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography level="body-md">SMS Alerts</Typography>
            <Switch
              checked={formData.notify_via_sms}
              onChange={(e) => setFormData((prev) => ({ ...prev, notify_via_sms: e.target.checked }))}
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography level="body-md">Push Notifications</Typography>
            <Switch
              checked={formData.notify_via_push}
              onChange={(e) => setFormData((prev) => ({ ...prev, notify_via_push: e.target.checked }))}
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Quiet Hours Start</FormLabel>
              <Input
                type="time"
                value={formData.quiet_hours_start || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, quiet_hours_start: e.target.value || null }))}
              />
            </FormControl>
            <FormControl sx={{ flex: 1 }}>
              <FormLabel>Quiet Hours End</FormLabel>
              <Input
                type="time"
                value={formData.quiet_hours_end || ''}
                onChange={(e) => setFormData((prev) => ({ ...prev, quiet_hours_end: e.target.value || null }))}
              />
            </FormControl>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="plain" color="neutral" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" loading={isPending}>
            {isAuto ? 'Save Alert Settings' : isEditing ? 'Save Changes' : 'Create Territory'}
          </Button>
        </DialogActions>
      </form>
    </>
  );
};

// Outer component - FormContent only renders when open, so it remounts with fresh state
export const TerritoryForm: React.FC<TerritoryFormProps> = ({
  open,
  onClose,
  onSuccess,
  territory,
}) => {
  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: 600, width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        {open && (
          <FormContent
            key={territory?.id ?? 'new'}
            territory={territory}
            onClose={onClose}
            onSuccess={onSuccess}
          />
        )}
      </ModalDialog>
    </Modal>
  );
};

export default TerritoryForm;
